// Common
import { type HttpContext } from '@angular/common/http';
import { Component, inject, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';

// Types
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { StateKey } from '@modules/settings/types/state-key';
import { type TasksRailScope } from '@modules/tasks/components/sidebar-context-menu/tasks-rail-menu';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { VirtualFolder } from '@modules/tasks/types/virtual-folder';

// Services

// RX
import { finalize, takeUntil } from 'rxjs/operators';

// Components
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';
import { ProjectListStateService } from '@modules/tasks/services/project-list-state.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { ProjectQuickFormComponent } from '@modules/elements/components/quick-forms/project-quick-form/project-quick-form.component';
import { ProjectsService } from '@modules/tasks/services/projects.service';

@Component({
  selector: 'app-side-bar-projects',
  templateUrl: './side-bar-projects.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-projects.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [
    ...ProjectListStateService.providers({
      stateKey: StateKey.sidebarProjectsListState,
    }),
  ],
})
export class SideBarProjectsComponent
  extends BaseSidebarContainersItemComponent<Project, Task, ProjectsFilters, VirtualFolder>
  implements OnInit, OnChanges
{
  @Input() public title = 'Projects';
  @Input() public depth?: number;
  @Input() public tenantId: string | null | undefined;
  /** A workspace section of the rail: the header gets the prototype menu, an options button and a semantic plus. */
  @Input() public rail = false;
  /** Whether the section is the member's My Workspace or the Team Workspace. */
  @Input() public railScope: TasksRailScope = 'mine';

  /** The workspace the header's buttons name, as the prototype words it ("My" / "Team"). */
  protected get railWorkspace(): string {
    return this.railScope === 'team' ? 'Team' : 'My';
  }

  protected quickProject = new Project();
  private createInProgress = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.tenantId) this.quickProject = new Project({ accountId: this.tenantId ?? undefined });
  }

  application = Application.tasking;
  sidebarSplitViewKey = SidebarSplitViewKey.tasking;

  private readonly projectListStateService = inject(ProjectListStateService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly projectsService = inject(ProjectsService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = toObservable(this.displayTimeZoneService.timeZone);

  public override ngOnInit() {
    super.ngOnInit();

    combineLatest([this.projectListStateService.value(), this.displayTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        this.filters = new ProjectsFilters({ displayTimeZone }).applyListState(state);
      });
  }

  protected createProject(project: Project, quickForm?: ProjectQuickFormComponent): void {
    if (this.createInProgress) return;
    this.createInProgress = true;
    const requestContext = this.requestContext();
    this.projectsService
      .create(project, { requestContext })
      .pipe(
        takeUntil(this.alive),
        finalize(() => {
          this.createInProgress = false;
        }),
      )
      .subscribe({
        next: () => this.quickFormPopoverClose.next(),
        error: (error) => quickForm?.handleError(error),
      });
  }

  protected override railRequestContext(): HttpContext | undefined {
    return this.requestContext();
  }

  private requestContext(): HttpContext | undefined {
    return this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId);
  }
}
